import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Layout } from '../../components/Layout';
import { useIsMobile } from '../../hooks/useIsMobile';
import { createEstimate } from './api';
const emptyMeasurement = () => ({ label: '', value: '', unit: '' });
export const EstimateCreate = () => {
    const navigate = useNavigate();
    const isMobile = useIsMobile();
    const [form, setForm] = useState({
        customerName: '',
        customerId: '',
        street: '',
        city: '',
        state: '',
        zip: '',
        projectDescription: '',
        requirements: '',
        measurements: [],
        notes: '',
    });
    const [errors, setErrors] = useState({});
    const [saving, setSaving] = useState(false);
    const [serverError, setServerError] = useState(null);
    const setField = (field, value) => {
        setForm((prev) => ({ ...prev, [field]: value }));
        if (errors[field]) {
            setErrors((prev) => ({ ...prev, [field]: undefined }));
        }
    };
    const addMeasurement = () => {
        setForm((prev) => ({ ...prev, measurements: [...prev.measurements, emptyMeasurement()] }));
    };
    const removeMeasurement = (idx) => {
        setForm((prev) => ({
            ...prev,
            measurements: prev.measurements.filter((_, i) => i !== idx),
        }));
    };
    const updateMeasurement = (idx, field, value) => {
        setForm((prev) => {
            const updated = prev.measurements.map((m, i) => i === idx ? { ...m, [field]: value } : m);
            return { ...prev, measurements: updated };
        });
    };
    const validate = () => {
        const errs = {};
        if (!form.customerName.trim())
            errs.customerName = 'Customer name is required';
        if (!form.projectDescription.trim())
            errs.projectDescription = 'Project description is required';
        if (!form.street.trim())
            errs.street = 'Street is required';
        if (!form.city.trim())
            errs.city = 'City is required';
        if (!form.state.trim())
            errs.state = 'State is required';
        if (!form.zip.trim())
            errs.zip = 'ZIP is required';
        setErrors(errs);
        return Object.keys(errs).length === 0;
    };
    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!validate())
            return;
        setSaving(true);
        setServerError(null);
        try {
            const estimate = await createEstimate({
                customerName: form.customerName,
                customerId: form.customerId || undefined,
                siteAddress: {
                    street: form.street,
                    city: form.city,
                    state: form.state,
                    zip: form.zip,
                },
                projectDescription: form.projectDescription,
                requirements: form.requirements,
                measurements: form.measurements.filter((m) => m.label.trim()),
                notes: form.notes,
            });
            navigate(`/estimates/${estimate.id}`);
        }
        catch (e) {
            setServerError(e.message ?? 'Failed to create estimate');
        }
        finally {
            setSaving(false);
        }
    };
    const sectionStyle = {
        backgroundColor: 'rgba(255,255,255,0.5)',
        backdropFilter: 'blur(20px)',
        WebkitBackdropFilter: 'blur(20px)',
        border: '1px solid rgba(255,255,255,0.8)',
        borderRadius: '20px',
        boxShadow: '0 8px 32px rgba(0,0,0,0.08)',
        padding: isMobile ? '20px 16px' : '28px 32px',
        marginBottom: '20px',
    };
    const sectionHeadingStyle = {
        fontSize: '17px',
        fontWeight: '600',
        color: '#1d1d1f',
        margin: '0 0 20px 0',
        paddingBottom: '12px',
        borderBottom: '1px solid rgba(0,0,0,0.08)',
    };
    const labelStyle = {
        fontSize: '13px',
        fontWeight: '600',
        color: '#3c3c43',
        marginBottom: '6px',
        display: 'block',
    };
    const inputStyle = {
        width: '100%',
        padding: '11px 14px',
        fontSize: '15px',
        border: '1px solid rgba(0,0,0,0.12)',
        borderRadius: '12px',
        backgroundColor: 'rgba(255,255,255,0.7)',
        color: '#1d1d1f',
        outline: 'none',
        boxSizing: 'border-box',
        minHeight: '44px',
    };
    const errorInputStyle = {
        ...inputStyle,
        border: '1px solid rgba(255,59,48,0.5)',
    };
    const fieldGroup = {
        marginBottom: '16px',
    };
    const errorMsg = {
        fontSize: '12px',
        color: '#c0392b',
        marginTop: '4px',
    };
    const rowStyle = {
        display: 'grid',
        gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr',
        gap: '16px',
    };
    return (_jsx(Layout, { children: _jsxs("div", { style: { maxWidth: '760px', margin: '0 auto' }, children: [_jsxs("div", { style: { marginBottom: '32px' }, children: [_jsx("button", { style: {
                                background: 'none',
                                border: 'none',
                                color: '#0071e3',
                                fontSize: '14px',
                                cursor: 'pointer',
                                padding: '0 0 8px 0',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '4px',
                            }, onClick: () => navigate('/estimates'), children: "\u2190 Back to Estimates" }), _jsx("h2", { style: {
                                fontSize: '42px',
                                fontWeight: '700',
                                margin: '8px 0 8px 0',
                                color: '#1d1d1f',
                                letterSpacing: '-0.5px',
                            }, children: "New Estimate" }), _jsx("p", { style: { fontSize: '17px', color: '#86868b', margin: 0 }, children: "Fill in the details to create a new estimate" })] }), serverError && (_jsx("div", { style: {
                        padding: '14px 20px',
                        backgroundColor: 'rgba(255,59,48,0.1)',
                        border: '1px solid rgba(255,59,48,0.3)',
                        borderRadius: '12px',
                        color: '#c0392b',
                        fontSize: '14px',
                        marginBottom: '20px',
                    }, children: serverError })), _jsxs("form", { onSubmit: handleSubmit, children: [_jsxs("div", { style: sectionStyle, children: [_jsx("h3", { style: sectionHeadingStyle, children: "Customer" }), _jsxs("div", { style: rowStyle, children: [_jsxs("div", { style: fieldGroup, children: [_jsx("label", { style: labelStyle, children: "Customer Name *" }), _jsx("input", { style: errors.customerName ? errorInputStyle : inputStyle, type: "text", value: form.customerName, onChange: (e) => setField('customerName', e.target.value), placeholder: "Full name or company" }), errors.customerName && _jsx("p", { style: errorMsg, children: errors.customerName })] }), _jsxs("div", { style: fieldGroup, children: [_jsx("label", { style: labelStyle, children: "Customer ID (optional)" }), _jsx("input", { style: inputStyle, type: "text", value: form.customerId, onChange: (e) => setField('customerId', e.target.value), placeholder: "Existing customer ID" })] })] }), _jsx("div", { style: { marginBottom: '12px' }, children: _jsx("label", { style: sectionHeadingStyle, children: "Site Address" }) }), _jsxs("div", { style: fieldGroup, children: [_jsx("label", { style: labelStyle, children: "Street *" }), _jsx("input", { style: errors.street ? errorInputStyle : inputStyle, type: "text", value: form.street, onChange: (e) => setField('street', e.target.value), placeholder: "123 Main St" }), errors.street && _jsx("p", { style: errorMsg, children: errors.street })] }), _jsxs("div", { style: {
                                        display: 'grid',
                                        gridTemplateColumns: isMobile ? '1fr 1fr' : '2fr 1fr 1fr',
                                        gap: '16px',
                                    }, children: [_jsxs("div", { style: fieldGroup, children: [_jsx("label", { style: labelStyle, children: "City *" }), _jsx("input", { style: errors.city ? errorInputStyle : inputStyle, type: "text", value: form.city, onChange: (e) => setField('city', e.target.value), placeholder: "City" }), errors.city && _jsx("p", { style: errorMsg, children: errors.city })] }), _jsxs("div", { style: fieldGroup, children: [_jsx("label", { style: labelStyle, children: "State *" }), _jsx("input", { style: errors.state ? errorInputStyle : inputStyle, type: "text", value: form.state, onChange: (e) => setField('state', e.target.value), placeholder: "CA", maxLength: 2 }), errors.state && _jsx("p", { style: errorMsg, children: errors.state })] }), _jsxs("div", { style: fieldGroup, children: [_jsx("label", { style: labelStyle, children: "ZIP *" }), _jsx("input", { style: errors.zip ? errorInputStyle : inputStyle, type: "text", value: form.zip, onChange: (e) => setField('zip', e.target.value), placeholder: "90210" }), errors.zip && _jsx("p", { style: errorMsg, children: errors.zip })] })] })] }), _jsxs("div", { style: sectionStyle, children: [_jsx("h3", { style: sectionHeadingStyle, children: "Project Details" }), _jsxs("div", { style: fieldGroup, children: [_jsx("label", { style: labelStyle, children: "Project Description *" }), _jsx("textarea", { style: {
                                                ...(errors.projectDescription ? errorInputStyle : inputStyle),
                                                minHeight: '100px',
                                                resize: 'vertical',
                                            }, value: form.projectDescription, onChange: (e) => setField('projectDescription', e.target.value), placeholder: "Describe the project scope..." }), errors.projectDescription && (_jsx("p", { style: errorMsg, children: errors.projectDescription }))] }), _jsxs("div", { style: fieldGroup, children: [_jsx("label", { style: labelStyle, children: "Requirements" }), _jsx("textarea", { style: { ...inputStyle, minHeight: '80px', resize: 'vertical' }, value: form.requirements, onChange: (e) => setField('requirements', e.target.value), placeholder: "Special requirements or client requests..." })] })] }), _jsxs("div", { style: sectionStyle, children: [_jsx("h3", { style: sectionHeadingStyle, children: "Measurements" }), form.measurements.length === 0 && (_jsx("p", { style: { color: '#86868b', fontSize: '14px', marginBottom: '12px' }, children: "No measurements added yet." })), form.measurements.map((m, idx) => (_jsxs("div", { style: {
                                        display: 'grid',
                                        gridTemplateColumns: isMobile ? '1fr 1fr' : '2fr 1fr 1fr auto',
                                        gap: '10px',
                                        marginBottom: '10px',
                                        alignItems: 'start',
                                    }, children: [_jsxs("div", { children: [_jsx("label", { style: labelStyle, children: "Label" }), _jsx("input", { style: inputStyle, type: "text", value: m.label, onChange: (e) => updateMeasurement(idx, 'label', e.target.value), placeholder: "e.g. Window Width" })] }), _jsxs("div", { children: [_jsx("label", { style: labelStyle, children: "Value" }), _jsx("input", { style: inputStyle, type: "text", value: m.value, onChange: (e) => updateMeasurement(idx, 'value', e.target.value), placeholder: "e.g. 36" })] }), _jsxs("div", { children: [_jsx("label", { style: labelStyle, children: "Unit" }), _jsx("input", { style: inputStyle, type: "text", value: m.unit, onChange: (e) => updateMeasurement(idx, 'unit', e.target.value), placeholder: "in / ft / sqft" })] }), _jsx("div", { style: { paddingTop: isMobile ? '0' : '24px' }, children: _jsx("button", { type: "button", onClick: () => removeMeasurement(idx), style: {
                                                    padding: '11px 14px',
                                                    backgroundColor: 'rgba(255,59,48,0.1)',
                                                    color: '#c0392b',
                                                    border: 'none',
                                                    borderRadius: '12px',
                                                    cursor: 'pointer',
                                                    fontWeight: '600',
                                                    minHeight: '44px',
                                                    width: isMobile ? '100%' : 'auto',
                                                }, children: "\u00D7" }) })] }, idx))), _jsx("button", { type: "button", onClick: addMeasurement, style: {
                                        padding: '10px 20px',
                                        backgroundColor: 'rgba(0,113,227,0.08)',
                                        color: '#0071e3',
                                        border: '1px dashed rgba(0,113,227,0.3)',
                                        borderRadius: '12px',
                                        cursor: 'pointer',
                                        fontSize: '14px',
                                        fontWeight: '600',
                                        minHeight: '44px',
                                    }, children: "+ Add Measurement" })] }), _jsxs("div", { style: sectionStyle, children: [_jsx("h3", { style: sectionHeadingStyle, children: "Notes" }), _jsxs("div", { style: fieldGroup, children: [_jsx("label", { style: labelStyle, children: "Internal Notes" }), _jsx("textarea", { style: { ...inputStyle, minHeight: '80px', resize: 'vertical' }, value: form.notes, onChange: (e) => setField('notes', e.target.value), placeholder: "Internal notes, reminders, follow-ups..." })] })] }), _jsxs("div", { style: {
                                display: 'flex',
                                gap: '12px',
                                justifyContent: 'flex-end',
                                marginBottom: '40px',
                            }, children: [_jsx("button", { type: "button", onClick: () => navigate('/estimates'), style: {
                                        padding: '12px 24px',
                                        backgroundColor: 'rgba(255,255,255,0.7)',
                                        border: '1px solid rgba(0,0,0,0.12)',
                                        borderRadius: '980px',
                                        fontSize: '15px',
                                        fontWeight: '500',
                                        color: '#1d1d1f',
                                        cursor: 'pointer',
                                        minHeight: '44px',
                                    }, children: "Cancel" }), _jsx("button", { type: "submit", disabled: saving, style: {
                                        padding: '12px 28px',
                                        backgroundColor: saving ? '#86868b' : '#0071e3',
                                        color: '#fff',
                                        border: 'none',
                                        borderRadius: '980px',
                                        fontSize: '15px',
                                        fontWeight: '600',
                                        cursor: saving ? 'not-allowed' : 'pointer',
                                        minHeight: '44px',
                                    }, children: saving ? 'Creating…' : 'Create Estimate' })] })] })] }) }));
};
//# sourceMappingURL=EstimateCreate.js.map